/**
 * Staleness nudge banner for the signed-in candidate dashboard (M09 S04 T4.2).
 *
 * Shows one calm line when the profile was last updated more than 30 days
 * ago and the strength score is below 100. Display is capped to once per
 * 30 days via localStorage key `bp-stale-nudge` (last shown timestamp).
 * Dashboard only — never rendered on the public profile page.
 *
 * Visibility is decided synchronously in the `useState` initializer (the
 * PWA test suite renders via `renderToStaticMarkup`, which never runs
 * effects); the initializer doubles as the cap stamp.
 *
 * @see sprints/2026-10/Milestone_09_BluePagesCandidateGamification/sprint-04-nudges-closeout
 */
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

const STALE_AFTER_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const CAP_KEY = 'bp-stale-nudge';

/** Pure staleness rule — exported for unit tests. */
export function isStaleProfile(
  lastUpdatedAt: string | null | undefined,
  score: number | undefined,
  now: number = Date.now(),
): boolean {
  if (!lastUpdatedAt || score === undefined || score >= 100) return false;
  const updated = Date.parse(lastUpdatedAt);
  if (Number.isNaN(updated)) return false;
  return now - updated > STALE_AFTER_MS;
}

/** Pure cap rule — show when never shown or last shown over 30 days ago. */
export function isCapOpen(lastShownAt: string | null | undefined, now: number = Date.now()): boolean {
  if (!lastShownAt) return true;
  const shown = Date.parse(lastShownAt);
  if (Number.isNaN(shown)) return true;
  return now - shown > STALE_AFTER_MS;
}

/**
 * Full display decision: stale + cap open. Stamps the cap key when it
 * returns true (idempotent per mount — one banner per 30 days).
 */
export function shouldShowStaleBanner(
  lastUpdatedAt: string | null | undefined,
  score: number | undefined,
  now: number = Date.now(),
): boolean {
  if (!isStaleProfile(lastUpdatedAt, score, now)) return false;
  try {
    if (!isCapOpen(localStorage.getItem(CAP_KEY), now)) return false;
    localStorage.setItem(CAP_KEY, new Date(now).toISOString());
    return true;
  } catch {
    return true; // storage unavailable — show anyway (cap degrades)
  }
}

interface StaleProfileBannerProps {
  lastUpdatedAt: string | null | undefined;
  score: number | undefined;
}

export function StaleProfileBanner({ lastUpdatedAt, score }: StaleProfileBannerProps) {
  const history = useHistory();
  const [visible] = useState(() => shouldShowStaleBanner(lastUpdatedAt, score));

  if (!visible) return null;

  return (
    <div
      data-testid="stale-profile-banner"
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        margin: '0 0 12px',
        padding: '10px 14px',
        borderRadius: 10,
        background: 'rgba(251, 191, 36, 0.12)',
        border: '1px solid rgba(251, 191, 36, 0.45)',
        fontSize: '0.85rem',
        color: '#fbbf24',
      }}
    >
      <span>
        Your profile has not changed in a while. A quick update helps you stay visible to employers.
      </span>
      <button
        type="button"
        data-testid="stale-profile-cta"
        onClick={() => history.push('/profile/edit')}
        style={{
          flexShrink: 0,
          padding: '6px 12px',
          borderRadius: 8,
          border: '1px solid rgba(251, 191, 36, 0.6)',
          background: 'transparent',
          color: '#fbbf24',
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Update profile
      </button>
    </div>
  );
}
